import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  QMS_ONLYOFFICE_SERVER_URL,
  buildQmsOnlyOfficeViewConfig,
} from "../qms/qmsOnlyOfficeViewConfig";
import {
  describeOnlyOfficePreviewError,
  downloadQmsOriginalDocument,
} from "../qms/qmsDocumentDownloadUtils";
import "../qms/QmsPages.scss";

const DOCS_API_SCRIPT_ID = "qms-standalone-onlyoffice-api";
const EDITOR_MOUNT_ID = "qms-standalone-onlyoffice-editor";
const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "webp"]);

function getUrlFromQuery(search) {
  const raw = String(search || "");
  if (!raw) return "";

  const params = new URLSearchParams(raw.startsWith("?") ? raw : `?${raw}`);
  const fromParam = String(params.get("url") || "").trim();
  if (fromParam) {
    try {
      return decodeURIComponent(fromParam.replace(/\+/g, " "));
    } catch {
      return fromParam;
    }
  }

  if (raw.startsWith("?url=")) {
    const value = raw.slice(5);
    try {
      return decodeURIComponent(value.replace(/\+/g, " "));
    } catch {
      return value;
    }
  }

  return "";
}

function getFileNameFromUrl(url) {
  if (!url || typeof url !== "string") return "document";
  try {
    const path = url.split("?")[0];
    const segments = path.split("/");
    let name = segments[segments.length - 1] || "document";
    try {
      name = decodeURIComponent(name);
    } catch {
      // keep raw segment
    }
    return name;
  } catch {
    return "document";
  }
}

function getExtensionFromFileName(fileName) {
  const name = String(fileName || "").trim();
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0 || lastDot === name.length - 1) return "";
  return name.slice(lastDot + 1).toLowerCase();
}

function loadOnlyOfficeApi() {
  if (typeof window !== "undefined" && window.DocsAPI) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(DOCS_API_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Could not load OnlyOffice DocsAPI script.")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = DOCS_API_SCRIPT_ID;
    script.src = `${QMS_ONLYOFFICE_SERVER_URL}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Could not load OnlyOffice DocsAPI script."));
    document.head.appendChild(script);
  });
}

function QMSpreview() {
  const [searchParams] = useSearchParams();
  const editorRef = useRef(null);
  const [status, setStatus] = useState("Loading preview…");
  const [error, setError] = useState("");

  const documentUrl = useMemo(
    () => getUrlFromQuery(searchParams.toString()),
    [searchParams],
  );
  const documentTitle = useMemo(
    () => getFileNameFromUrl(documentUrl),
    [documentUrl],
  );
  const documentExtension = useMemo(
    () => getExtensionFromFileName(documentTitle),
    [documentTitle],
  );
  const isImage = IMAGE_EXTS.has(documentExtension);

  useEffect(() => {
    let cancelled = false;

    const destroyEditor = () => {
      if (
        editorRef.current &&
        typeof editorRef.current.destroyEditor === "function"
      ) {
        try {
          editorRef.current.destroyEditor();
        } catch {
          /* ignore */
        }
      }
      editorRef.current = null;
      const mount = document.getElementById(EDITOR_MOUNT_ID);
      if (mount) mount.innerHTML = "";
    };

    if (!documentUrl || isImage) {
      destroyEditor();
      setStatus("");
      setError("");
      return undefined;
    }

    setStatus("Loading OnlyOffice…");
    setError("");

    const start = async () => {
      try {
        await loadOnlyOfficeApi();
        if (cancelled) return;
        if (!window.DocsAPI) {
          throw new Error("DocsAPI is not defined.");
        }

        destroyEditor();
        const mount = document.getElementById(EDITOR_MOUNT_ID);
        if (!mount) return;

        const config = buildQmsOnlyOfficeViewConfig({
          extension: documentExtension,
          fileTitle: documentTitle,
          documentUrl,
          documentKeyPrefix: "qms_standalone",
        });

        if (!config.document?.url) {
          throw new Error("Document URL is not valid for OnlyOffice.");
        }

        editorRef.current = new window.DocsAPI.DocEditor(EDITOR_MOUNT_ID, {
          ...config,
          events: {
            onReady: () => {
              if (!cancelled) setStatus("");
            },
            onDocumentReady: () => {
              if (!cancelled) setStatus("");
            },
            onError: (event) => {
              if (cancelled) return;
              setStatus("");
              setError(describeOnlyOfficePreviewError(event?.data?.errorCode));
            },
          },
        });
      } catch (err) {
        if (!cancelled) {
          setStatus("");
          setError(err?.message || "Could not open OnlyOffice preview.");
        }
      }
    };

    const timer = window.setTimeout(() => {
      void start();
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      destroyEditor();
    };
  }, [documentUrl, documentExtension, documentTitle, isImage]);

  const handleDownloadOriginal = () => {
    downloadQmsOriginalDocument({
      documentUrl,
      fileName: documentTitle,
    });
  };

  return (
    <div className="qms-standalone-preview-page">
      {documentUrl ? (
        <>
          <div className="qms-doc-preview-modal__toolbar aq-doc-preview__toolbar">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary qms-doc-preview-modal__download-btn"
              onClick={handleDownloadOriginal}
              title="Download the original uploaded file"
            >
              <i className="fas fa-download me-1" aria-hidden="true" />
              Download original
            </button>
            <span className="qms-doc-preview-modal__toolbar-hint">
              Downloads the original file
              {documentExtension ? ` (.${documentExtension})` : ""}.
            </span>
          </div>
          {isImage ? (
            <div className="qms-standalone-preview-page__image-wrap">
              <img
                src={documentUrl}
                alt={documentTitle}
                className="qms-standalone-preview-page__image"
              />
            </div>
          ) : (
            <>
              {status ? (
                <div className="qms-standalone-preview-page__status">
                  {status}
                </div>
              ) : null}
              {error ? (
                <div className="qms-standalone-preview-page__error">{error}</div>
              ) : null}
              <div
                id={EDITOR_MOUNT_ID}
                className="qms-standalone-preview-page__editor"
              />
            </>
          )}
        </>
      ) : (
        <div className="qms-standalone-preview-page__empty">
          <p>No document URL provided.</p>
          <p className="qms-standalone-preview-page__hint">
            Open with: /qmspreview?url=
            {encodeURIComponent("https://your-document-url")}
          </p>
        </div>
      )}
    </div>
  );
}

export default QMSpreview;
